<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast, blankOrValue } from "$app/utils"
  import InlineInput from "$app/components/InlineInput.svelte"
  import LoadingSpinner from "$app/components/LoadingSpinner.svelte"
  import {
    ChevronRight,
    FileText,
    FileLock,
    FileWarning,
    FileCheck2
  } from "lucide-svelte"

  import Progresses from "./Progress/Index.svelte"
  import EditGoalStatus from "./EditGoalStatus.svelte"
  import Attachments from "./Attachments/Index.svelte"
  import History from "./GoalDetails/History.svelte"
  import DatePicker from "$app/components/DatePicker.svelte"
  import _ from "lodash"
  import { isLeadership } from "$app/utils/goals.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import ToggleEdit from "$app/components/ToggleEdit.svelte"
  import { appStore } from "$app/store.svelte"

  const plan: PlanType = appStore.plan
  const current_user: UserType = appStore.current_user
  const editable = true
  interface Props {
    taskId: number
  }

  let { taskId }: Props = $props()

  let errors = {}

  let tab = $state(1)
  let isLoading = $state(true)
  let task: TaskType = $state()

  const departments: any[] = appStore.departments
  const users: any[] = appStore.users

  let name = $state("")
  let description = $state("")
  let action_plan = $state("")
  let leadership_notes = $state("")
  let obstacles_text = $state("")

  let show_action_plan = $state(false)

  let parentGoals: GoalType[] = $state([])

  const currentDate = new Date()
  let expected_at = $state(null)
  let completed_at = $state(null)

  async function initializeForm(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(`/plans/${plan.id}/tasks/${taskId}/edit.json`)

      task = resp.data.task as TaskType
      if (task.expected_at) {
        try {
          expected_at = new Date(`${task.expected_at}T00:00:00`)
        } catch (e) {
          expected_at = null
        }
      }

      if (task.completed_at) {
        try {
          completed_at = new Date(`${task.completed_at}T00:00:00`)
        } catch (e) {
          completed_at = null
        }
      }

      name = task.name
      description = task.description
      action_plan = task.action_plan
      leadership_notes = task.leadership_notes
      obstacles_text = task.obstacles_text
      checkActionPlan()

      parentGoals = _.sortBy(
        resp.data.quarterly_goals as GoalType[],
        "assignee_position"
      )
    } catch (e) {
      toast("There was an error loading the task", "error")
    } finally {
      isLoading = false
    }
  }

  function checkActionPlan() {
    // Parse Action Plan to remove HTML Tags to see if it has actual text content
    let ap_html = action_plan
    let ap_div = document.createElement("div")
    ap_div.innerHTML = ap_html
    let ap_text = ap_div.textContent || ap_div.innerText || ""

    show_action_plan = ap_text.length > 0 || false
  }

  let saving = $state(false)

  function updateStore(data: TaskType): void {
    const idx = appStore.tasks.findIndex((t) => t.id === task.id)
    try {
      data.expected_at = new Date(`${data.expected_at}T00:00:00`)
      data.completed_at = new Date(`${data.completed_at}T00:00:00`)
    } catch (e) {}
    appStore.tasks[idx] = data
    task = data
  }

  async function handleSave(
    payload: { [key: string]: any },
    field?: string
  ): Promise<void> {
    saving = true
    try {
      const resp = await xhr.patch<TaskType>(
        `/plans/${plan.id}/tasks/${taskId}.json`,
        {
          task: payload
        }
      )
      updateStore(resp.data)
    } catch (e: any) {
      console.error(e)
      toast(`There was an error updating ${field ? field : ""}`, "error")
      if (e.response) {
        errors = { ...errors, ...e.response.data.errors }
      }
    } finally {
      saving = false
    }
  }

  function updateParentGoal(): void {
    handleSave({ goal_id: task.goal_id })
    const parentGoal = parentGoals.find((g) => g.id === task.goal_id)
    task.parent_name = parentGoal.name
  }

  const invalidUsersMsg =
    "There are members that do not belong to Leadership Team, please remove them in the Members tab."
  let invalidVisibility = $state(false)

  $effect(() => {
    if (task?.visibility === "leadership" && task?.assignee_type === "User") {
      const us = users.find((u) => u.id === task.assignee_id)

      if (!isLeadership(us.role)) {
        invalidVisibility = true
        toast(invalidUsersMsg, "error", { duration: 10000 })
      }
    } else {
      invalidVisibility = false
    }
  })

  onMount(async () => {
    initializeForm()
  })
</script>

<div class="mt-2 overflow-visible">
  <div>
    {#if isLoading}
      <LoadingSpinner />
    {:else}
      <div class="mb-3">
        <div
          class="inline-flex items-center rounded bg-quarterly-goal px-2 py-0.5 text-xs font-medium text-gray-800"
        >
          Parent Quarterly Goal
          <ChevronRight class="text-gray-500 mx-2 w-3 h-3" />
          <span class="font-bold">{task.parent_name}</span>
        </div>
      </div>
      <div class="grid grid-cols-12 gap-6">
        <div
          class="flex items-center py-3 col-span-12 {editable
            ? 'md:col-span-7'
            : ''} "
        >
          <div class="text-base text-gray-900 w-full">
            <label for="name" class="label text-sm text-gray-600">
              Task Name
            </label>
            <div class="text-lg">
              <InlineInput
                bind:value={name}
                onsuccess={(data: TaskType) => updateStore(data)}
                postURL={`/plans/${plan.id}/tasks/${task.id}.json`}
                name="name"
                model="task"
              />
            </div>
          </div>
        </div>

        {#if editable}
          <div class="col-span-12 sm:col-span-6 lg:col-span-3">
            <label for="parent_goal_id" class="label text-sm text-gray-600">
              Parent Goal
            </label>
            <div class="input-cont">
              <select
                id="parent_goal_id"
                bind:value={task.goal_id}
                onchange={() => updateParentGoal()}
              >
                {#if parentGoals.length}
                  {#each departments as dept (dept.id)}
                    <optgroup label={dept.name}>
                      {#each parentGoals.filter((g) => g.assignee_id === dept.id) as pg (pg.id)}
                        <option value={pg.id}>{pg.name}</option>
                      {/each}
                    </optgroup>
                  {/each}
                {:else}
                  <option value="">No Parent Goals for Expected Date</option>
                {/if}
              </select>
            </div>
          </div>
          {#if current_user.role === "leader" || current_user.role === "admin"}
            <div class="col-span-12 sm:col-span-6 lg:col-span-2">
              <label for="visibility" class="label text-sm text-gray-600">
                Task Visibility
              </label>
              {#if task.parent_visibility == "plan"}
                <div class="input-cont">
                  <select
                    id="visibility"
                    bind:value={task.visibility}
                    onchange={() => handleSave({ visibility: task.visibility })}
                  >
                    <option value="plan">All Members</option>
                    <option value="leadership">Only Leadership Team</option>
                  </select>
                </div>
              {:else}
                <div
                  class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
                >
                  Parent is Only Leadership Team
                </div>
              {/if}
            </div>
          {/if}
        {/if}
      </div>
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-6 lg:col-span-3">
          <label for="status" class="label text-sm text-gray-600">Status</label>
          <div class="w-full">
            <EditGoalStatus
              goalOrTask={task}
              type="Task"
              editable={true}
              onupdate={(data) => updateStore(data)}
            />
          </div>
        </div>

        <div class="col-span-6 lg:col-span-4 lg:pl-4">
          <label for="status" class="label text-sm text-gray-600"
            >Assignee</label
          >
          <div class="mt-1 text-sm">
            <select
              id="assignee_id"
              value={`${task.assignee_type}-${task.assignee_id}`}
              onchange={(e) => {
                let assignee_type = e.target.value.split("-")[0]
                let assignee_id = e.target.value.split("-")[1]

                handleSave(
                  { assignee_id: assignee_id, assignee_type: assignee_type },
                  "Assignee"
                )
              }}
            >
              <optgroup label="Departments">
                {#each departments as dept (dept.id)}
                  <option value={`Department-${dept.id}`}>{dept.name}</option>
                {/each}
              </optgroup>
              <optgroup label="Members">
                {#each users as user (user.id)}
                  <option value={`User-${user.id}`}>{user.name}</option>
                {/each}
              </optgroup>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 lg:col-span-5 lg:col-start-8">
          <div>
            <label for="expected_at" class="label text-sm text-gray-600">
              Expected On
            </label>
            <DatePicker
              date={expected_at}
              onchange={(d) => handleSave({ expected_at: d })}
            />
          </div>
          <div>
            <label for="completed" class="label text-sm text-gray-600">
              Completed On
            </label>

            <DatePicker
              date={completed_at}
              onchange={(d) => handleSave({ completed_at: d })}
            />
          </div>
        </div>

        <div class="col-span-12">
          <div>
            <div>
              <div class="border-b border-gray-200">
                <nav class="-mb-px flex tabs" aria-label="Tabs">
                  <button
                    onclick={() => (tab = 1)}
                    class:active={tab === 1}
                    class="tab"
                  >
                    <div class="whitespace-nowrap px-6">Task Details</div>
                  </button>
                  <button
                    onclick={() => (tab = 2)}
                    class:active={tab === 2}
                    class="tab"
                  >
                    <div class="whitespace-nowrap px-6">Task Progress</div>
                  </button>
                  <button
                    onclick={() => (tab = 3)}
                    class:active={tab === 3}
                    class="tab"
                  >
                    <div class="whitespace-nowrap px-6">History</div>
                  </button>
                  <button
                    onclick={() => (tab = 4)}
                    class:active={tab === 4}
                    class="tab"
                  >
                    <div class="whitespace-nowrap px-6">Attachments</div>
                  </button>
                </nav>
              </div>
            </div>
          </div>
          <div
            class="px-4 py-5 sm:p-6 border rounded-sm"
            style="min-height: 26rem;"
          >
            <div class="col-span-12">
              {#if tab === 1}
                <div class="col-span-12 mb-4">
                  <p class="flex items-center mb-1">
                    <FileText class="text-gray-400 mr-2 h-4 w-4" />
                    Description
                  </p>
                  <ToggleEdit
                    onblur={() => {
                      handleSave({ description: description }, "Description")
                    }}
                  >
                    {#snippet editcont()}
                      <div>
                        <Tiptap
                          id="edit-description"
                          buttons={onlyTextButtons}
                          content={task.description}
                          onupdate={({ html }) => (description = html)}
                        />
                      </div>
                    {/snippet}
                    {#snippet showcont()}
                      <div class="border px-4 py-2 shadow-md ProseMirror">
                        {@html blankOrValue(
                          description,
                          "Enter a description and/or action plan for this task."
                        )}
                      </div>
                    {/snippet}
                  </ToggleEdit>
                </div>

                {#if current_user.role === "leader" || current_user.role === "admin"}
                  <div class="col-span-12 mb-4">
                    <h3 class="flex items-center mb-1">
                      <FileLock class="text-gray-400 mr-2 h-4 w-4" />
                      Leadership Notes
                    </h3>
                    <ToggleEdit
                      onblur={() => {
                        handleSave(
                          { leadership_notes: leadership_notes },
                          "Leadership Notes"
                        )
                      }}
                    >
                      {#snippet editcont()}
                        <div>
                          <Tiptap
                            id="edit-leadership-notes"
                            buttons={onlyTextButtons}
                            content={task.leadership_notes}
                            onupdate={({ html }) => (leadership_notes = html)}
                          />
                        </div>
                      {/snippet}
                      {#snippet showcont()}
                        <div class="border px-4 py-2 shadow-md ProseMirror">
                          {@html blankOrValue(
                            leadership_notes,
                            "Enter any private notes here which are visible only for Leadership Team members."
                          )}
                        </div>
                      {/snippet}
                    </ToggleEdit>
                  </div>
                {/if}

                {#if show_action_plan}
                  <div class="col-span-12 mb-4">
                    <p class="flex items-center mb-1">
                      <FileCheck2 class="text-gray-400 mr-2 h-4 w-4" />
                      Action Plan
                    </p>
                    <ToggleEdit
                      onblur={() =>
                        handleSave({ action_plan: action_plan }, "Action Plan")}
                    >
                      {#snippet editcont()}
                        <div>
                          <Tiptap
                            id="edit-action-plan"
                            buttons={onlyTextButtons}
                            content={task.action_plan}
                            onupdate={({ html }) => (action_plan = html)}
                          />
                        </div>
                      {/snippet}
                      {#snippet showcont()}
                        <div class="border px-4 py-2 shadow-md ProseMirror">
                          {@html blankOrValue(
                            action_plan,
                            "Enter your action plan for accomplishing this task"
                          )}
                        </div>
                      {/snippet}
                    </ToggleEdit>
                  </div>
                {/if}

                <div class="col-span-12">
                  <p class="flex items-center mb-1">
                    <FileWarning class="text-gray-400 mr-2 h-4 w-4" />
                    Obstacles
                  </p>
                  <ToggleEdit
                    onblur={() =>
                      handleSave(
                        { obstacles_text: obstacles_text },
                        "Obstacles"
                      )}
                  >
                    {#snippet editcont()}
                      <div>
                        <Tiptap
                          id="edit-obstacles-text"
                          buttons={onlyTextButtons}
                          content={task.obstacles_text}
                          on:update={({ html }) => (obstacles_text = html)}
                        />
                      </div>
                    {/snippet}
                    {#snippet showcont()}
                      <div class="border px-4 py-2 shadow-md ProseMirror">
                        {@html blankOrValue(
                          obstacles_text,
                          "Enter any obstacles to accomplishing this task"
                        )}
                      </div>
                    {/snippet}
                  </ToggleEdit>
                </div>
              {:else if tab === 2}
                <Progresses
                  id={taskId}
                  type="task"
                  oncreate={(data) => {
                    task.progress_date = new Date(data.created_at)
                  }}
                />
              {:else if tab === 3}
                <History id={task.id} type="Task" />
              {:else if tab === 4}
                <Attachments id={task.id} type="Task" />
              {/if}
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  :root {
    --toastHeight: auto;
  }
</style>
